/**
 * T1020cr · Lead Scoring & Intent Matrix
 *
 * Each lead is scored on three weighted axes: volume, risk, and
 * recency.  The operator can slide each weight live and the Hot /
 * Warm / Cold tier distribution recalculates instantly.  Score = Σ
 * (weight × normalized axis value).
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import ActiveCardShell from "./ActiveCardShell";
import { scoreLead, tierOf } from "./scoring";
import { Gauge, Flame, Snowflake, Wind, ArrowRight } from "lucide-react";

const TIER_META = {
  hot:  { label: "Hot",  Icon: Flame,     cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  warm: { label: "Warm", Icon: Wind,      cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  cold: { label: "Cold", Icon: Snowflake, cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
};

export default function LeadScoringCard({ scoringPool, onPushToPipeline, onWeightsChange }) {
  const [w, setW] = useState({ volume: 50, risk: 20, recency: 30 });

  // Keep the parent in sync so the MetricsBar's "Hot" tile reflects
  // the operator's weight tweaks live.
  const updateW = (patch) => {
    const next = { ...w, ...patch };
    setW(next);
    onWeightsChange?.(next);
  };

  const scored = useMemo(
    () => scoringPool.map((l) => {
      const score = scoreLead(l, w);
      return { ...l, score, tier: tierOf(score) };
    }).sort((a, b) => b.score - a.score),
    [scoringPool, w]
  );

  const dist = useMemo(() => {
    const d = { hot: 0, warm: 0, cold: 0 };
    scored.forEach((l) => { d[l.tier] += 1; });
    return d;
  }, [scored]);

  const total = scored.length || 1;

  return (
    <ActiveCardShell
      id="T1020cr"
      kicker="SCORING MATRIX"
      title="Lead Scoring & Intent Matrix"
      Icon={Gauge}
    >
      {/* Weight sliders */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["volume","risk","recency"].map((k) => (
          <label key={k} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
                {k} weight
              </span>
              <span className="font-mono text-xs text-brand-cyan">{w[k]}</span>
            </div>
            <input
              data-testid={`veroogle-t1020-weight-${k}`}
              type="range"
              min={0}
              max={100}
              value={w[k]}
              onChange={(e) => updateW({ [k]: Number(e.target.value) })}
              className="w-full accent-brand-cyan"
            />
          </label>
        ))}
      </div>

      {/* Tier distribution bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Tier distribution</span>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">{total} leads</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-white/5" data-testid="veroogle-t1020-dist">
          <div className="bg-rose-500/80" style={{ width: `${(dist.hot/total)*100}%` }} />
          <div className="bg-amber-500/80" style={{ width: `${(dist.warm/total)*100}%` }} />
          <div className="bg-sky-500/80" style={{ width: `${(dist.cold/total)*100}%` }} />
        </div>
        <div className="flex items-center gap-4 mt-2 font-mono text-[10px] tracking-[0.14em] uppercase">
          <span className="text-rose-300">Hot {dist.hot}</span>
          <span className="text-amber-300">Warm {dist.warm}</span>
          <span className="text-sky-300">Cold {dist.cold}</span>
        </div>
      </div>

      {/* Scored list */}
      <div className="mt-5 rounded-xl border border-white/8 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="veroogle-t1020-table">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 border-b border-white/8">
                <th className="px-4 py-2 font-normal">Merchant</th>
                <th className="px-4 py-2 font-normal text-right">Est. Monthly</th>
                <th className="px-4 py-2 font-normal text-right">Score</th>
                <th className="px-4 py-2 font-normal">Tier</th>
                <th className="px-4 py-2 font-normal text-right">·</th>
              </tr>
            </thead>
            <tbody>
              {scored.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-xs">Push leads from T1010cr to begin scoring.</td></tr>
              )}
              {scored.map((lead, i) => {
                const meta = TIER_META[lead.tier];
                const Icon = meta.Icon;
                return (
                  <tr key={i} data-testid={`veroogle-t1020-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.03] transition">
                    <td className="px-4 py-2.5 text-white font-medium">{lead.name}</td>
                    <td className="px-4 py-2.5 text-right text-slate-400 font-mono">${lead.volume.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-brand-cyan font-mono">{lead.score}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-mono text-[9px] tracking-[0.18em] uppercase ${meta.cls}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => { onPushToPipeline?.(lead); toast.success(`${lead.name} pushed to pipeline`); }}
                        data-testid={`veroogle-t1020-push-${i}`}
                        className="inline-flex items-center gap-1 text-brand-emerald hover:text-emerald-300 font-mono text-[10px] tracking-[0.14em] uppercase transition"
                      >
                        Pipeline <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ActiveCardShell>
  );
}
